import React, { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import classnames from "classnames";

import HorizontalScrollable from "@/base/components/Global/HorizontalScrollable";
import useDebounce from "@/base/hooks/useDebounce";
import useWindowSize from "@/base/hooks/useWindowSize";
import {
  type CategoryListItem,
  ProductListItemType,
  type SectionListItem,
} from "@/store/products/types";
import { useScrollContext } from "@/store/useScrollContext";
import { getBrandTheme } from "@/theme";

import CategoriesList from "./CategoriesList";
import ItemList from "./ItemList";
import {
  CATEGORIES_SELECTOR_HEIGHT,
  ITEM_MARGIN_BOTTOM,
  SECTION_TITLE_HEIGHT,
  SECTIONS_SELECTOR_HEIGHT,
} from "./ProductList";

const useStyles = createUseStyles(
  ({ color, borderRadius, spacing, font, mediaQuery }) => ({
    section: {
      paddingTop: spacing.x4l,
      paddingBottom: spacing.xxl,
      paddingLeft: spacing.xxl,
      fontSize: font.size.x4l,
      fontWeight: font.weight.s,
      lineHeight: font.lineHeight.xxl,
      color: color.topCategoriesHeadingText,
    },
    sectionItem: {
      display: "inline-block",
      fontSize: font.size.xs,
      fontWeight: font.weight.l,
      lineHeight: font.lineHeight.xxs,
      padding: [spacing.l, spacing.xxl],
      color: color.subCategoriesText,
      position: "relative",
      textDecoration: "none",
      borderRadius: borderRadius.button,
    },
    sectionItemActive: {
      color: color.subCategoriesTextActive,
    },
    subItemsBar: {
      position: "sticky",
      zIndex: 1,
      transition: "300ms top ease",
      margin: "0 -16px",
      padding: "0 16px",
      backgroundColor: color.subCategoriesBg,
    },
    subItems: {
      width: "inherit",
      overflowX: "auto",
      overflowY: "hidden",
      whiteSpace: "nowrap",
      overflow: "-moz-scrollbars-none",
      "-ms-overflow-style": "none",
      "&::-webkit-scrollbar": {
        width: "0 !important",
        height: "0 !important",
      },
      display: "flex",
      gap: spacing.s,
      justifyContent: "start",
      alignItems: "center",
      paddingTop: spacing.s,
      paddingBottom: spacing.s,
    },
    fixedSectionContainer: {
      display: "flex",
      flexDirection: "column",
    },
    fixedSectionEmptyContainer: {
      paddingTop: SECTIONS_SELECTOR_HEIGHT,
    },
    productListContainer: {
      marginBottom: 0,
      display: "flex",
      flexWrap: "wrap",
      gap: spacing.xxl,

      [mediaQuery.lg]: {
        marginRight: -spacing.xxl,
      },
    },
    featuredItemWrapper: {
      display: "flex",
      flexDirection: "row",
      gap: "0.5rem",
    },
  }),
);

type ItemProps = {
  item: SectionListItem;
  setActiveSection?: (item: SectionListItem) => void;
  isSectionActive: boolean;
  activeSectionBg?: string | undefined;
  showShadows: boolean;
};

const SectionItem = ({
  item,
  setActiveSection,
  isSectionActive,
  showShadows = false,
}: ItemProps) => {
  const [isFixed, setIsFixed] = useState(false);
  const sectionContainer = useRef<HTMLDivElement>(null);
  const categoriesContainer = useRef<HTMLDivElement>(null);
  const [activeCategory, _setActiveCategory] =
    useState<CategoryListItem | null>(null);
  const activeCategoryRef = React.useRef(activeCategory);
  const setActiveCategory = (newActiveCategory: CategoryListItem | null) => {
    activeCategoryRef.current = newActiveCategory;
    _setActiveCategory(newActiveCategory);
  };
  const debouncedActiveCategoryRef = useDebounce(activeCategoryRef, 50);
  const [{ values }] = useScrollContext();

  const [left, setLeft] = useState<number>(0);
  const debouncedLeft = useDebounce(left, 50);
  const theme = getBrandTheme();

  const [isScrollActive, _setIsScrollActive] = useState(true);
  const isScrollActiveRef = React.useRef(isScrollActive);
  const setIsScrollActive = (newValue: boolean) => {
    isScrollActiveRef.current = newValue;
    _setIsScrollActive(newValue);
  };
  const { isMdAndUp } = useWindowSize();

  const valuesRef = React.useRef(values);

  useEffect(() => {
    // This is necessary to access state values in event listeners (which are closed over initialState)
    valuesRef.current = values;
  }, [values]);

  const classes = useStyles();

  const categories = item.items.filter(
    (i) => i.type === ProductListItemType.CATEGORY,
  );
  const hasCategories = categories.length > 0;

  useEffect(() => {
    if (activeCategory === null && hasCategories) {
      setActiveCategory(categories[0]);
    }
  }, [item]);

  useEffect(() => {
    const onScroll = () => {
      // Too expensive of a function to be called _per_ section for _each_ scroll event.

      if (sectionContainer.current) {
        const sectionContainerRect =
          sectionContainer.current.getBoundingClientRect();

        // TODO: the name of this variable is lying. It stays true if you scroll way below the section.
        const isSectionVisible =
          sectionContainerRect.top <= SECTIONS_SELECTOR_HEIGHT + 50;

        // TODO: the name of this variable is lying. It stays true if you scroll way below the category.
        const isCategoryVisible =
          sectionContainerRect.top <=
          SECTIONS_SELECTOR_HEIGHT -
            SECTION_TITLE_HEIGHT +
            (valuesRef.current.shouldShowNavbar ? 48 : 0);

        const hasLeftSection =
          -sectionContainerRect.top >=
          sectionContainerRect.height -
            SECTIONS_SELECTOR_HEIGHT +
            ITEM_MARGIN_BOTTOM;

        const isBelowCategory =
          -sectionContainerRect.top >=
          sectionContainerRect.height -
            SECTIONS_SELECTOR_HEIGHT -
            CATEGORIES_SELECTOR_HEIGHT;

        const isItemVisible = isSectionVisible && !hasLeftSection;

        if (isItemVisible && setActiveSection) {
          setActiveSection(item);
        }

        if (hasCategories) {
          if (!isCategoryVisible && !isBelowCategory) {
            setIsFixed(false);
          } else if (isCategoryVisible && !isBelowCategory) {
            setIsFixed(true);
          } else if (isCategoryVisible && isBelowCategory) {
            setIsFixed(false);
          }
        }
      }
    };
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (categoriesContainer.current) {
      categoriesContainer.current.scrollTo({
        left: debouncedLeft,
        behavior: "smooth",
      });
    }
  }, [debouncedLeft]);

  useEffect(() => {
    if (activeCategory && isSectionActive && categoriesContainer.current) {
      const activeCategoryChildren = categoriesContainer.current.children;
      let newLeft = 0;
      if (activeCategoryChildren) {
        for (const element of activeCategoryChildren) {
          const firstClass = element.classList[0];
          const secondClass = element.classList[1];
          if (secondClass?.includes("sectionItemActive")) {
            const { width } = element.getBoundingClientRect() || {};
            if (width) {
              newLeft += width * 0.5 - 32;
            }
            break;
          }
          if (firstClass?.includes("sectionItem")) {
            const { width } = element.getBoundingClientRect() || {};
            if (width) {
              newLeft += width + 8; // width + paddingLeft
            }
          }
        }

        const listElement = document.querySelector("#section-selectors-list");
        if (listElement) {
          const halfListWidth = listElement.getBoundingClientRect().width / 2;
          newLeft -= halfListWidth;
        }
      }
      setLeft(newLeft);
    }
  }, [activeCategory]);

  return (
    <div
      className={classnames(
        isFixed &&
          (hasCategories
            ? classes.fixedSectionContainer
            : classes.fixedSectionEmptyContainer),
      )}
      ref={sectionContainer}
    >
      <div id={`section-${item.id}`} />
      <div className={classes.section}>{item.title || "-"}</div>

      {hasCategories ? (
        <>
          <div
            className={classes.subItemsBar}
            style={{ top: `${values.fixedSubItemTop}px` }}
          >
            <HorizontalScrollable
              scrollContainerRef={categoriesContainer}
              buttonHeight={40}
              shouldShowArrows={isMdAndUp}
            >
              <div
                className={classnames(classes.subItems, "sub-sections")}
                ref={categoriesContainer}
              >
                {categories.map((category) => (
                  <a
                    style={{
                      backgroundColor: item.bgColor,
                    }}
                    key={`category-link-${item.id}-${category.id}`}
                    className={classnames(
                      classes.sectionItem,
                      category.id === activeCategory?.id &&
                        classes.sectionItemActive,
                    )}
                    onClick={(e) => {
                      setIsScrollActive(false);
                      e.preventDefault();
                      const wrapper = document.querySelector(
                        `#category-${item.id}-${category.id}`,
                      );
                      if (wrapper) {
                        const bodyRect = document.body.getBoundingClientRect();
                        const wrapperRect = wrapper.getBoundingClientRect();
                        const offset = wrapperRect.top - bodyRect.top;
                        const top = isMdAndUp
                          ? Number(offset) -
                            SECTIONS_SELECTOR_HEIGHT -
                            CATEGORIES_SELECTOR_HEIGHT -
                            (wrapperRect.top < 0
                              ? theme.config.desktopNavbarHeight
                              : 0) +
                            54
                          : Number(offset) -
                            SECTIONS_SELECTOR_HEIGHT -
                            CATEGORIES_SELECTOR_HEIGHT -
                            (wrapperRect.top < 0
                              ? theme.config.mobileNavbarHeight
                              : 0) +
                            8;

                        window.scrollTo({
                          top,
                          behavior: "smooth",
                        });
                        setActiveCategory(category);
                      }
                      setTimeout(() => {
                        setIsScrollActive(true);
                      }, 1000);
                    }}
                    href={`#category-${item.id}-${category.id}`}
                  >
                    {category.title}
                  </a>
                ))}
              </div>
            </HorizontalScrollable>
          </div>
          <CategoriesList
            sectionId={item.id}
            categories={categories}
            activeCategoryRef={debouncedActiveCategoryRef}
            isScrollActiveRef={isScrollActiveRef}
            setActiveCategory={setActiveCategory}
            showShadows={showShadows}
          />
        </>
      ) : (
        <div className={classes.productListContainer} ref={categoriesContainer}>
          <ItemList
            items={item.items.filter(
              (i) =>
                i.type === ProductListItemType.ITEM ||
                i.type === ProductListItemType.VARIABLE,
            )}
            keyGenerator={(itemId, i) =>
              `product-item-category-${itemId}-${item.uniqueId}-${i}`
            }
            showShadows={showShadows}
          />
        </div>
      )}
    </div>
  );
};

type CategoriesListProps = {
  sections: SectionListItem[];
  setActiveSection?: (item: SectionListItem) => void;
  activeSection?: SectionListItem | null;
  activeSectionBg?: string | undefined;
  showShadows?: boolean;
};

const SectionsList: React.FC<CategoriesListProps> = ({
  sections,
  setActiveSection,
  activeSection,
  showShadows = false,
}) => {
  return (
    <>
      {sections.map((section) => (
        <SectionItem
          item={section}
          key={`section-block-item-${section.id}`}
          setActiveSection={setActiveSection}
          isSectionActive={
            activeSection ? activeSection.id === section.id : false
          }
          showShadows={showShadows}
        />
      ))}
    </>
  );
};

export default SectionsList;
